import React, { useState } from 'react';
import { motion } from 'framer-motion';
import JordanMap from '../components/JordanMap';
import ExpertList from '../components/ExpertList';
import SearchFilters from '../components/SearchFilters';
import ExpertModal from '../components/ExpertModal';
import { useExpertContext } from '../context/ExpertContext';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiMapPin, FiBookOpen, FiLoader } = FiIcons;

const HomePage = () => {
  const { experts, filteredExperts, searchExperts, loading } = useExpertContext();
  const { t } = useLanguage();
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleExpertSelect = (expert) => {
    setSelectedExpert(expert);
    setShowModal(true);
  };

  const approvedExperts = experts.filter(e => e.status === 'approved');
  
  const stats = [
    { icon: FiUsers, label: t('stats.total'), value: approvedExperts.length, color: 'text-green-600', bg: 'bg-green-100' },
    { icon: FiMapPin, label: t('stats.governorates'), value: new Set(approvedExperts.map(e => e.governorate)).size, color: 'text-blue-600', bg: 'bg-blue-100' },
    { icon: FiBookOpen, label: t('stats.topics'), value: new Set(approvedExperts.flatMap(e => e.support_topics || [])).size, color: 'text-purple-600', bg: 'bg-purple-100' }
  ];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-4">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
          <SafeIcon icon={FiLoader} className="text-4xl text-green-600" />
        </motion.div>
        <p className="text-slate-500 font-bold animate-pulse uppercase tracking-widest text-xs">Initializing Network...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-12">
        <h1 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight mb-6 leading-tight">
          {t('hero.title')}
        </h1>
        <p className="text-xl text-slate-500 mb-10 max-w-3xl mx-auto font-medium leading-relaxed">
          {t('hero.subtitle')}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {stats.map((stat, index) => (
            <motion.div 
              key={stat.label} 
              initial={{ y: 20, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              transition={{ delay: index * 0.1 }} 
              className="expert-card flex flex-col items-center justify-center p-8 bg-white"
            >
              <div className={`w-14 h-14 ${stat.bg} rounded-2xl flex items-center justify-center mb-4`}>
                <SafeIcon icon={stat.icon} className={`text-2xl ${stat.color}`} />
              </div>
              <div className="text-4xl font-black text-slate-900">{stat.value}</div>
              <div className="text-slate-400 font-black uppercase text-[10px] tracking-widest mt-2">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <div className="mb-8">
        <SearchFilters onSearch={searchExperts} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 h-[600px]">
          <JordanMap experts={filteredExperts} onExpertSelect={handleExpertSelect} />
        </div>
        <div className="lg:col-span-4 h-[600px]">
          <ExpertList experts={filteredExperts} onExpertSelect={handleExpertSelect} />
        </div>
      </div>

      {showModal && selectedExpert && (
        <ExpertModal expert={selectedExpert} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default HomePage;